import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Dropdown, Input, Modal, Select, Skeleton, Table, Tabs, Tag, message } from "antd";
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, EllipsisOutlined, EnvironmentOutlined, FilterOutlined, InfoCircleOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useRecoilState, useRecoilValue } from "recoil";
import { useNavigate } from "react-router-dom";
import { brandKitsPageState, brandKitsState, locationsState, memberState } from "../../../../state/atoms";
import BrandKitApi from "../../../../api/BrandKitApi";
import { BrandKitDto } from "../../../../generated/dashboard-client";
import { Button as MhmButton } from "../../misc/Button";
import BrandKitIcon from "../../../icons/BrandKitIcon";
import { env } from "../../../../env/env";
import { AssignBrandToLocationModal } from "./AssignBrandToLocationModal";
import { LocationAssignmentsTab, buildKitByLocation } from "./LocationAssignmentsTab";
import "./BrandManagementListPage.scss";
import { isDesignSubscription, isProSubscription } from "../../../../service/BillingService";

const ALL_VALUE = -1;
const PAGE_SIZE = 100;

const WELCOME_BANNER_KEY = "brand-management-welcome-dismissed";
const WELCOME_BANNER_END = new Date("2026-10-01T00:00:00");

function shouldShowWelcomeBanner(): boolean {
  if (new Date() >= WELCOME_BANNER_END) return false;
  try {
    return window.localStorage.getItem(WELCOME_BANNER_KEY) !== "1";
  } catch {
    return true;
  }
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function BrandManagementListPage() {
  const navigate = useNavigate();
  const member = useRecoilValue(memberState);
  const teamId = member?.teamId || "";
  const locations = useRecoilValue(locationsState);

  const [brandKits, setBrandKits] = useRecoilState(brandKitsState);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState<number[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);

  const [chooseOpen, setChooseOpen] = useState(false);
  const [createMode, setCreateMode] = useState<"single" | "perLocation">("single");
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const [perLocationOpen, setPerLocationOpen] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);

  const [rowActionKit, setRowActionKit] = useState<BrandKitDto | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [page, setPage] = useRecoilState(brandKitsPageState);
  const [welcomeVisible, setWelcomeVisible] = useState<boolean>(shouldShowWelcomeBanner);

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const renameInputRef = useRef<any>(null);

  const startRename = (kit: BrandKitDto) => {
    if (!kit.id) return;
    setRenamingId(kit.id);
    setRenameValue(kit.name || "");
    setTimeout(() => renameInputRef.current?.select(), 0);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };

  const handleRename = () => {
    if (renameSaving) return;
    const name = renameValue.trim();
    const kit = brandKits.find((b) => b.id === renamingId);
    if (!kit?.id || !name || name === (kit.name || "")) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    BrandKitApi.update(kit.id, { ...kit, name })
      .then((resp) => {
        setBrandKits(brandKits.map((b) => (b.id === resp.data.id ? resp.data : b)));
        setRenamingId(null);
        setRenameValue("");
        message.success("Brand kit renamed");
      })
      .catch(() => message.error("Failed to rename brand kit"))
      .finally(() => setRenameSaving(false));
  };

  const dismissWelcome = () => {
    setWelcomeVisible(false);
    try {
      window.localStorage.setItem(WELCOME_BANNER_KEY, "1");
    } catch {}
  };

  const canManageBrand = !!member && (
    member.owner || member.role === "Admin" || (member as any).manageBrand
  );

  useEffect(() => {
    if (member && !canManageBrand) {
      navigate(`${env.dashboardPath}/account`, { replace: true });
    }
  }, [member, canManageBrand, navigate]);

  useEffect(() => {
    if (!teamId) return;
    setLoading(true);
    BrandKitApi.listForTeam(teamId)
      .then((resp) => setBrandKits(resp.data || []))
      .catch(() => message.error("Failed to load brand kits"))
      .finally(() => setLoading(false));
  }, [teamId, setBrandKits]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return brandKits.filter((b) => {
      if (q && !(b.name || "").toLowerCase().includes(q)) return false;
      if (locationFilter.length > 0) {
        const locIds = b.locationIds || [];
        if (!locationFilter.some((id) => locIds.includes(id))) return false;
      }
      return true;
    });
  }, [brandKits, query, locationFilter]);

  const filtersInitialized = useRef(false);
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    setPage(1);
  }, [query, locationFilter, setPage]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    if (page > maxPage) setPage(maxPage);
  }, [filtered.length, page, setPage]);

  const pageItems = useMemo(() => {
    const sorted = [...filtered].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
    );
    return sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filtered, page]);

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    BrandKitApi.create({ teamId, name })
      .then((resp) => {
        setBrandKits([...brandKits, resp.data]);
        setCreateOpen(false);
        setNewName("");
        message.success("Brand kit created");
        if (resp.data.id) {
          navigate(`${env.dashboardPath}/account/brand-management/${resp.data.id}`);
        }
      })
      .catch(() => message.error("Failed to create brand kit"))
      .finally(() => setSaving(false));
  };

  const allLocationIds = locations.map((l) => l.internalId!);
  const allLocationsSelected =
    allLocationIds.length > 0 && allLocationIds.every((id) => locationFilter.includes(id));

  const openAssignFor = (kit: BrandKitDto) => {
    setRowActionKit(kit);
    setAssignOpen(true);
  };

  const openDeleteFor = (kit: BrandKitDto) => {
    setRowActionKit(kit);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!rowActionKit?.id) return;
    const kitId = rowActionKit.id;
    setDeleting(true);
    BrandKitApi.delete(kitId)
      .then(() => {
        setBrandKits(brandKits.filter((b) => b.id !== kitId));
        setDeleteOpen(false);
        setRowActionKit(null);
        message.success("Brand kit deleted");
      })
      .catch(() => message.error("Failed to delete brand kit"))
      .finally(() => setDeleting(false));
  };

  const handleLocationFilterChange = (vals: number[]) => {
    if (vals.includes(ALL_VALUE)) {
      setLocationFilter(allLocationsSelected ? [] : allLocationIds);
      return;
    }
    setLocationFilter(vals);
  };

  const filterTagRender = (props: any) => {
    if (allLocationsSelected) {
      if (props.value !== allLocationIds[0]) return <span style={{ display: "none" }} />;
      return (
        <Tag
          closable
          onClose={(e: any) => {
            e.preventDefault();
            setLocationFilter([]);
          }}
          style={{ marginRight: 3 }}
        >
          All locations
        </Tag>
      );
    }
    return (
      <Tag
        closable
        onClose={(e: any) => {
          e.preventDefault();
          props.onClose();
        }}
        style={{ marginRight: 3 }}
      >
        {props.label}
      </Tag>
    );
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      onCell: () => ({ className: "brand-management-name-cell" }),
      render: (name: string, record: BrandKitDto) => {
        const label = name || "Untitled";
        const logoPath = (record as any).firstLogoPath as string | undefined;
        const logoUrl = logoPath ? `${env.s3Path}/${logoPath}` : null;
        const initial = label.trim().charAt(0).toUpperCase() || "?";
        const isRenaming = renamingId === record.id;
        return (
          <div className="brand-management-name-link">
            {logoUrl ? (
              <img className="brand-management-name-avatar" src={logoUrl} alt={label} loading="lazy" />
            ) : (
              <span className="brand-management-name-avatar placeholder">{initial}</span>
            )}
            {isRenaming ? (
              <span
                className="brand-management-name-rename"
                onClick={(e) => e.stopPropagation()}
              >
                <Input
                  ref={renameInputRef}
                  className="brand-management-name-rename-input"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onPressEnter={handleRename}
                  onKeyDown={(e) => e.key === "Escape" && cancelRename()}
                  onBlur={handleRename}
                  disabled={renameSaving}
                />
              </span>
            ) : (
              <span className="brand-management-name-text-wrap">
                <span className="brand-management-name-text">{label}</span>
                <EditOutlined
                  className="brand-management-name-edit-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    startRename(record);
                  }}
                />
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Locations",
      key: "locationsCount",
      width: 140,
      render: (_: any, record: BrandKitDto) => (record.locationIds || []).length,
    },
    {
      title: "Assets",
      key: "assetsCount",
      width: 120,
      render: (_: any, record: BrandKitDto) => {
        const count = (record as any).assetCount;
        return typeof count === "number" ? count : "—";
      },
    },
    {
      title: "Templates",
      key: "templatesCount",
      width: 120,
      render: (_: any, record: BrandKitDto) => {
        const count = (record as any).templateCount;
        return typeof count === "number" ? count : "—";
      },
    },
    {
      title: "Last Updated",
      dataIndex: "updatedDate",
      key: "updatedDate",
      width: 180,
      render: (date: string) => formatDate(date),
    },
    {
      title: "",
      key: "actions",
      width: 60,
      align: "right" as const,
      render: (_: any, record: BrandKitDto) => (
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          overlayClassName="brand-management-row-menu"
          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          menu={{
            items: [
              {
                key: "assign",
                label: "Manage Location Assignment",
                icon: <EnvironmentOutlined />,
                onClick: ({ domEvent }) => {
                  domEvent.stopPropagation();
                  openAssignFor(record);
                },
              },
              { type: "divider" },
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: ({ domEvent }) => {
                  domEvent.stopPropagation();
                  openDeleteFor(record);
                },
              },
            ],
          }}
        >
          <Button
            type="text"
            icon={<EllipsisOutlined />}
            className="brand-management-row-action"
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  function onClickAddNewBrandKit() {
    if (brandKits.length > 0 && !isProSubscription(member) && !member.diningRDIntegration) {
      if (isDesignSubscription(member)) {
        window.MHM.ProModal.switchToProWithOverview({
          memberId: member.id,
          onSuccess: () => window.location.reload()
        });
      } else {
        window.MHM.ProModal.upgrade({
          memberId: member.id,
          onSuccess: () => window.location.reload()
        });
      }
    } else {
      setCreateMode("single");
      setChooseOpen(true);
    }
  }

  const handleChooseNext = () => {
    setChooseOpen(false);
    if (createMode === "single") {
      setCreateOpen(true);
    } else {
      setPerLocationOpen(true);
    }
  };

  const perLocationPlan = useMemo(() => {
    const assigned = new Set<number>();
    brandKits.forEach((b) => (b.locationIds || []).forEach((id) => assigned.add(id)));
    const toCreate: { locationId: number; name: string }[] = [];
    const skipped: { locationId: number; name: string }[] = [];
    locations.forEach((loc) => {
      if (loc.internalId == null) return;
      const name = `${loc.name || "Location"} Brand`;
      (assigned.has(loc.internalId) ? skipped : toCreate).push({ locationId: loc.internalId, name });
    });
    return { toCreate, skipped };
  }, [locations, brandKits]);

  const handleBulkCreate = () => {
    const plan = perLocationPlan.toCreate;
    if (plan.length === 0) return;
    setBulkCreating(true);
    BrandKitApi.createBulk({
      teamId,
      items: plan.map((item) => ({ name: item.name, locationId: item.locationId })),
    })
      .then((resp) => {
        const createdCount = (resp.data || []).length;
        setPerLocationOpen(false);
        message.success(
          `${createdCount} Brand Kit${createdCount === 1 ? "" : "s"} created — one for each location`
        );
        return BrandKitApi.listForTeam(teamId).then((r) => setBrandKits(r.data || []));
      })
      .catch(() => message.error("Failed to create brand kits"))
      .finally(() => setBulkCreating(false));
  };

  const subtitle = `${filtered.length} ${filtered.length === 1 ? "Brand Kit" : "Brand Kits"} across all locations`;

  const unassignedCount = useMemo(() => {
    const map = buildKitByLocation(brandKits);
    return locations.reduce((n, l) => {
      if (l.internalId == null) return n;
      return map.has(l.internalId) ? n : n + 1;
    }, 0);
  }, [brandKits, locations]);

  const locationFilterCount = locationFilter.length;
  const locationFilterLabel = locationFilterCount === 0
    ? "Filter by location"
    : allLocationsSelected
      ? "All locations"
      : `${locationFilterCount} location${locationFilterCount > 1 ? "s" : ""}`;

  return (
    <div className="brand-management-list-page">
      {welcomeVisible && (
        <div className="brand-management-welcome-banner" role="status">
          <span className="brand-management-welcome-icon">
            <BrandKitIcon />
          </span>
          <div className="brand-management-welcome-text">
            <strong>Welcome to the new Brand Management tab!</strong>
            <span>Owners and Admins, you can manage your brand here.</span>
          </div>
          <button
            type="button"
            className="brand-management-welcome-close"
            onClick={dismissWelcome}
            aria-label="Dismiss"
          >
            <CloseOutlined />
          </button>
        </div>
      )}

      <header className="brand-management-list-header">
        <div className="brand-management-list-header-text">
          <h1>Brand Management</h1>
          <p className="brand-management-subtitle">{subtitle}</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="brand-management-new-btn"
          onClick={() => onClickAddNewBrandKit()}
        >
          Brand Kit
        </Button>
      </header>

      <Tabs
        className="brand-management-list-tabs"
        items={[
          {
            key: "kits",
            label: "Brand Kits",
            children: (
              <>
      <div className="brand-management-toolbar">
        <div className="brand-management-toolbar-left">
          <div className="brand-management-search-wrap">
            <Input
              className="brand-management-search"
              size="large"
              placeholder="Search brand kits..."
              prefix={<SearchOutlined style={{ color: "#999" }} />}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              allowClear
            />
            <Dropdown
              trigger={["click"]}
              placement="bottomRight"
              open={filterOpen}
              onOpenChange={setFilterOpen}
              dropdownRender={() => (
              <div className="brand-management-filter-dropdown">
                <Select
                  mode="multiple"
                  className="brand-management-filter-select"
                  style={{ width: "100%" }}
                  placeholder="Select locations"
                  value={locationFilter}
                  onChange={(vals) => handleLocationFilterChange(vals as number[])}
                  optionFilterProp="label"
                  optionLabelProp="label"
                  maxTagCount="responsive"
                  tagRender={filterTagRender}
                  menuItemSelectedIcon={null}
                  open
                  dropdownClassName="brand-management-filter-options"
                  getPopupContainer={(t) => t.parentElement as HTMLElement}
                >
                  <Select.Option value={ALL_VALUE} label="All locations" className="brand-management-filter-option">
                    <Checkbox checked={allLocationsSelected} />
                    <span>All locations</span>
                  </Select.Option>
                  {locations.map((loc) => (
                    <Select.Option
                      key={loc.internalId}
                      value={loc.internalId!}
                      label={loc.name}
                      className="brand-management-filter-option"
                    >
                      <Checkbox checked={locationFilter.includes(loc.internalId!)} />
                      <span>{loc.name}</span>
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}
          >
              <Button
                className={`brand-management-filter-btn${locationFilterCount > 0 ? " active" : ""}`}
                size="large"
                icon={<FilterOutlined />}
                aria-label={locationFilterLabel}
                title={locationFilterLabel}
              />
            </Dropdown>
          </div>
        </div>
      </div>

      {loading ? (
        <Skeleton active />
      ) : brandKits.length === 0 ? (
        <div className="brand-management-empty">
          <span className="brand-management-empty-icon"><BrandKitIcon /></span>
          <p>No brand kits yet</p>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setCreateMode("single");
              setChooseOpen(true);
            }}
          >
            Create Brand Kit
          </Button>
        </div>
      ) : (
        <Table
          className="brand-management-table"
          dataSource={pageItems}
          columns={columns}
          rowKey="id"
          pagination={
            filtered.length > PAGE_SIZE
              ? {
                  current: page,
                  pageSize: PAGE_SIZE,
                  total: filtered.length,
                  onChange: setPage,
                  showSizeChanger: false,
                }
              : false
          }
          locale={{ emptyText: "No brand kits match your filters" }}
          rowClassName="brand-management-row"
          onRow={(record) => ({
            onClick: () => {
              if (!record.id) return;
              navigate(`${env.dashboardPath}/account/brand-management/${record.id}`);
            },
          })}
        />
      )}
              </>
            ),
          },
          {
            key: "locations",
            label: (
              <span className="brand-management-tab-label">
                Location assignments
                {unassignedCount > 0 && (
                  <span className="brand-management-tab-badge">{unassignedCount} unassigned</span>
                )}
              </span>
            ),
            children: <LocationAssignmentsTab />,
          },
        ]}
      />

      <Modal
        className={"per-location-modal clean one"}
        open={perLocationOpen}
        width={600}
        onCancel={() => setPerLocationOpen(false)}
        footer={null}
      >
        <header>
          <h2>Create one per location</h2>
          <p className="per-location-sub">
            We'll create a Brand Kit for each location and assign it automatically.
            Locations that already have a Brand Kit are skipped.
          </p>
        </header>

        <div className="per-location-list">
          <div className="per-location-list-head">
            <span>
              {perLocationPlan.toCreate.length} Brand{" "}
              {perLocationPlan.toCreate.length === 1 ? "Kit" : "Kits"} will be created
            </span>
            <span>Auto-assigned</span>
          </div>
          {perLocationPlan.toCreate.map((item) => (
            <div className="per-location-list-row" key={item.locationId}>
              <span className="per-location-name">{item.name}</span>
              <span className="per-location-assigned">
                <CheckOutlined /> Assigned
              </span>
            </div>
          ))}
        </div>

        {perLocationPlan.skipped.length > 0 && (
          <div className="per-location-skip-list">
            {perLocationPlan.skipped.map((item) => (
              <div className="per-location-skip-note" key={item.locationId}>
                <InfoCircleOutlined /> {item.name} already exists and will be skipped.
              </div>
            ))}
          </div>
        )}

        <footer>
          <MhmButton type={"Cancel"} onClick={() => setPerLocationOpen(false)}>
            <span>Cancel</span>
          </MhmButton>
          <MhmButton
            type={"Primary"}
            onClick={handleBulkCreate}
            loading={bulkCreating}
            disabled={perLocationPlan.toCreate.length === 0}
          >
            <span>
              Create {perLocationPlan.toCreate.length} brand{" "}
              {perLocationPlan.toCreate.length === 1 ? "kit" : "kits"}
            </span>
          </MhmButton>
        </footer>
      </Modal>

      <Modal
        className={"choose-brand-mode-modal clean one"}
        open={chooseOpen}
        width={600}
        onCancel={() => setChooseOpen(false)}
        footer={null}
      >
        <header>
          <h2>Create new brand kit</h2>
          <p className="choose-brand-mode-sub">How would you like to set up your Brand Kit?</p>
        </header>

        <div className="choose-brand-mode-options">
          <button
            type="button"
            className={`choose-brand-mode-option${createMode === "single" ? " selected" : ""}`}
            onClick={() => setCreateMode("single")}
          >
            <span className="choose-brand-mode-radio" />
            <span className="choose-brand-mode-text">
              <strong>Create one Brand Kit</strong>
              <span>Name and set up a single Brand Kit. Best for accounts sharing one brand across all locations.</span>
            </span>
          </button>

          <button
            type="button"
            className={`choose-brand-mode-option${createMode === "perLocation" ? " selected" : ""}`}
            onClick={() => setCreateMode("perLocation")}
          >
            <span className="choose-brand-mode-radio" />
            <span className="choose-brand-mode-text">
              <strong>Create one per location</strong>
              <span>Automatically generates a Brand Kit for each location, named and assigned for you. Best for accounts where every location has its own brand.</span>
            </span>
          </button>
        </div>

        <footer>
          <MhmButton type={"Cancel"} onClick={() => setChooseOpen(false)}>
            <span>Cancel</span>
          </MhmButton>
          <MhmButton type={"Primary"} onClick={handleChooseNext}>
            <span>Next</span>
          </MhmButton>
        </footer>
      </Modal>

      <Modal
        className={"new-brand-modal clean one"}
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          setNewName("");
        }}
        footer={null}
      >
        <header>
          <h2>Create new brand kit</h2>
        </header>

        <div className={"new-brand-body"}>
          <Input
            size="large"
            placeholder="Name your brand kit"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={handleCreate}
            autoFocus
          />
        </div>

        <footer>
          <MhmButton
            type={"Cancel"}
            onClick={() => {
              setCreateOpen(false);
              setNewName("");
            }}
          >
            <span>Cancel</span>
          </MhmButton>
          <MhmButton
            type={"Primary"}
            onClick={handleCreate}
            loading={saving}
            disabled={!newName.trim()}
          >
            <span>Create</span>
          </MhmButton>
        </footer>
      </Modal>

      <AssignBrandToLocationModal
        open={assignOpen}
        brandKit={rowActionKit}
        onClose={() => {
          setAssignOpen(false);
          setRowActionKit(null);
        }}
      />

      <Modal
        className={"clean one"}
        open={deleteOpen}
        onCancel={() => {
          setDeleteOpen(false);
          setRowActionKit(null);
        }}
        footer={null}
      >
        <header>
          <h2>Are you sure?</h2>
        </header>

        <p>
          <b>{rowActionKit?.name || "This brand"}</b> will be removed and all its data will be lost.
        </p>

        <footer>
          <Button
            size={"large"}
            onClick={() => {
              setDeleteOpen(false);
              setRowActionKit(null);
            }}
          >
            Cancel
          </Button>
          <Button
            size={"large"}
            className={"primary"}
            onClick={handleDeleteConfirm}
            loading={deleting}
          >
            Delete
          </Button>
        </footer>
      </Modal>
    </div>
  );
}
